import { buildSectionSelector, normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, onDocumentReady } from '@theme/utilities';

/**
 * Hydrates a section using the Section Rendering API preserving states.
 * Only updates elements with data-hydration-key attribute. This ensures that non-targeted nodes do not have their markup/state updated unnecessarily.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
async function hydrateSection(sectionId, url, useCache = false) {
  const normalizedId = normalizeSectionId(sectionId);
  const section = document.getElementById(buildSectionSelector(normalizedId));

  if (!section || section.dataset.hydrated === 'true') {
    return;
  }

  await sectionRenderer.renderSection(normalizedId, { cache: useCache, url, mode: 'hydration' });

  section.dataset.hydrated = 'true';
}

/**
 * Runs a task as soon as the DOM is ready and exposes its completion state.
 * @param {() => Promise<void>} task
 * @returns {Promise<void>}
 */
function runWhenDocumentReady(task) {
  return new Promise((resolve, reject) => {
    onDocumentReady(() => {
      task().then(resolve).catch(reject);
    });
  });
}

/**
 * Hydrates immediately. Cached and in-flight Section Rendering responses are
 * reused so an interaction can take over a prefetch without another request.
 * @param {string} sectionId
 * @param {URL} [url]
 * @returns {Promise<void>}
 */
export function hydrateImmediate(sectionId, url) {
  return runWhenDocumentReady(() => hydrateSection(sectionId, url, true));
}

/**
 * Warms the Section Rendering cache without changing the current DOM.
 * @param {string} sectionId
 * @param {URL} [url]
 * @returns {Promise<void>}
 */
export function prefetchSection(sectionId, url) {
  return runWhenDocumentReady(async () => {
    const normalizedId = normalizeSectionId(sectionId);
    const section = document.getElementById(buildSectionSelector(normalizedId));

    if (!section || section.dataset.hydrated === 'true') return;

    await sectionRenderer.getSectionHTML(normalizedId, true, url);
  });
}

/**
 * Hydrates a section using the Section Rendering API preserving states, when
 * the DOM is ready.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
export async function hydrate(sectionId, url) {
  onDocumentReady(() => {
    requestIdleCallback(() => hydrateSection(sectionId, url));
  });
}
